import {
  hasCookieConsentForCategory,
  needsCookieConsent,
  saveCookieConsent,
  CookieConsentCategory,
} from "@flowstepdesign/shared";
import clsx from "clsx";
import { useCallback, useState } from "react";

import { config } from "@/config";
import { ROUTES } from "@/routes";

import { Button } from "./ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemSeparator,
  ItemTitle,
} from "./ui/item";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Switch } from "./ui/switch";

export const CookieConsentBar = () => {
  const needsConsent = needsCookieConsent();
  const hasAnalyticsConsent = hasCookieConsentForCategory(
    CookieConsentCategory.enum.analytics,
  );
  const hasMarketingConsent = hasCookieConsentForCategory(
    CookieConsentCategory.enum.marketing,
  );

  const [isSheetOpen, setSheetOpen] = useState<boolean>(false);
  const [analyticsConsent, setAnalyticsConsent] =
    useState<boolean>(hasAnalyticsConsent);
  const [marketingConsent, setMarketingConsent] =
    useState<boolean>(hasMarketingConsent);

  const handleSheetOpenChange = useCallback((open: boolean) => {
    setSheetOpen(open);
  }, []);

  const handleAnalyticsConsentChange = useCallback((consent: boolean) => {
    setAnalyticsConsent(consent);
  }, []);

  const handleMarketingConsentChange = useCallback((consent: boolean) => {
    setMarketingConsent(consent);
  }, []);

  const handleAcceptAll = useCallback(() => {
    saveCookieConsent(
      {
        necessary: true,
        analytics: true,
        marketing: true,
      },
      config.domain,
    );

    window.location.reload();
  }, []);

  const handleRejectNonEssential = useCallback(() => {
    saveCookieConsent(
      {
        necessary: true,
        analytics: false,
        marketing: false,
      },
      config.domain,
    );

    window.location.reload();
  }, []);

  const handleAcceptSelection = useCallback(() => {
    saveCookieConsent(
      {
        necessary: true,
        analytics: analyticsConsent,
        marketing: marketingConsent,
      },
      config.domain,
    );

    window.location.reload();
  }, [analyticsConsent, marketingConsent]);

  const handleManagePreferences = useCallback(() => {
    setSheetOpen(true);
  }, []);

  if (!needsConsent) {
    return null;
  }

  return (
    <>
      {!isSheetOpen && (
        <div
          id="cookie-consent-bar"
          className={clsx(
            "fixed bottom-2 right-2 z-[9999] w-[260px] rounded-2xl border border-[#F0F0F2] p-3",
            "text-foreground bg-white text-sm font-normal",
            "shadow-sm",
          )}
        >
          <p className="p-1 text-[#646573]">
            <span className="text-foreground">We use cookies</span> and similar
            technologies to run our platform, analyze usage, and for marketing.
            More in{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href={ROUTES.privacyPolicy}
              className="underline"
            >
              Privacy Policy
            </a>
            .
          </p>
          <div className="mt-3 flex w-full flex-col items-center justify-center gap-2">
            <Button
              size="sm"
              onClick={handleAcceptAll}
              className="w-full cursor-pointer"
            >
              Accept all
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleManagePreferences}
              className="w-full cursor-pointer"
            >
              Manage preferences
            </Button>
          </div>
        </div>
      )}

      <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent>
          <SheetHeader className="px-6">
            <SheetTitle>Manage Cookies Preferences</SheetTitle>
            <SheetDescription>
              You can change your preferences at any time. See our{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href={ROUTES.privacyPolicy}
                className="underline"
              >
                Privacy Policy
              </a>{" "}
              for more details.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col px-6">
            <Item className="px-0">
              <ItemContent>
                <ItemTitle className="text-base font-normal">
                  Strictly necessary cookies
                </ItemTitle>
                <ItemDescription className="line-clamp-none text-wrap">
                  Required for the platform to function properly, including
                  security, authentication, and session management.
                  <br />
                  Cannot be disabled.
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Switch checked disabled />
              </ItemActions>
            </Item>
            <ItemSeparator />
            <Item className="px-0">
              <ItemContent>
                <ItemTitle className="text-base font-normal">
                  Analytics cookies
                </ItemTitle>
                <ItemDescription className="line-clamp-none text-wrap">
                  Help us understand how users interact with the platform and
                  improve its performance and usability. Data is collected in
                  aggregated or pseudonymised form.
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Switch
                  checked={analyticsConsent}
                  onCheckedChange={handleAnalyticsConsentChange}
                  className="cursor-pointer"
                />
              </ItemActions>
            </Item>
            <ItemSeparator />
            <Item className="px-0">
              <ItemContent>
                <ItemTitle className="text-base font-normal">
                  Marketing cookies
                </ItemTitle>
                <ItemDescription className="line-clamp-none text-wrap">
                  May be used to support advertising and measure the
                  effectiveness of marketing campaigns. They may be set by us or
                  third-party partners.
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Switch
                  checked={marketingConsent}
                  onCheckedChange={handleMarketingConsentChange}
                  className="cursor-pointer"
                />
              </ItemActions>
            </Item>
          </div>
          <SheetFooter className="px-6">
            <Button className="cursor-pointer" onClick={handleAcceptSelection}>
              Save
            </Button>
            <Button
              className="cursor-pointer"
              variant="secondary"
              onClick={handleRejectNonEssential}
            >
              Reject non-essential
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};
